export const QUEST_JOURNAL_FILTER_AXIS = {
  INSTRUCTOR: "instructor",
  THEME: "theme",
};

export const QUEST_JOURNAL_INSTRUCTOR_KIND = {
  ANY: "any",
  STANDARD: "standard",
  SPECIAL: "special",
};

export const QUEST_JOURNAL_QUEST_OUTCOME = {
  ANY: "any",
  SUCCESS: "success",
  FAILED: "failed",
  IN_PROGRESS: "in_progress",
};

export const QUEST_JOURNAL_RELIC_OUTCOME = {
  ANY: "any",
  WITH_RELIC: "with_relic",
  WITHOUT_RELIC: "without_relic",
};

export const QUEST_JOURNAL_OBJECTIVE_KIND = {
  ANY: "any",
  READING: "reading",
  PLAY_GAMES: "play_games",
  SCORE_TARGET: "score_target",
  MASTERY: "mastery",
  DISCOVERY: "discovery",
  CAPTURE: "capture",
  MEMORY: "memory",
  GRADE_IMPROVE: "grade_improve",
};

export const QUEST_JOURNAL_REWARD_KIND = {
  ANY: "any",
  POTION: "potion",
  COMMON_GEM: "common_gem",
  PRECIOUS_GEM: "precious_gem",
};

export const QUEST_JOURNAL_COMPLETED_OBJECTIVES = {
  ANY: "any",
  ONE: "1",
  TWO: "2",
  ONE_OR_TWO: "1_or_2",
};

export const QUEST_JOURNAL_DATE_PRESET = {
  ALL: "all",
  CURRENT_MONTH: "current_month",
  CURRENT_WEEK: "current_week",
};

export const QUEST_JOURNAL_READING_VALIDATION = {
  VALIDATED: "validated",
  NOT_VALIDATED: "not_validated",
};

export const QUEST_JOURNAL_READING_VALIDATION_OPTIONS = [
  { id: QUEST_JOURNAL_READING_VALIDATION.VALIDATED, label: "Validé" },
  { id: QUEST_JOURNAL_READING_VALIDATION.NOT_VALIDATED, label: "Non validé" },
];

export const DEFAULT_READING_VALIDATION = QUEST_JOURNAL_READING_VALIDATION_OPTIONS.map(
  (option) => option.id
);

export const QUEST_JOURNAL_MASTERY_VARIANTS = [
  { id: "b-mastery-3", label: "+3 points" },
  { id: "b-mastery-4", label: "+4 points" },
  { id: "b-mastery-5", label: "+5 points" },
];

export const DEFAULT_MASTERY_TARGETS = QUEST_JOURNAL_MASTERY_VARIANTS.map((variant) => variant.id);

export const QUEST_JOURNAL_DISCOVERY_VARIANTS = [
  { id: "b-discovery-3", label: "+3 points" },
  { id: "b-discovery-4", label: "+4 points" },
  { id: "b-discovery-5", label: "+5 points" },
];

export const DEFAULT_DISCOVERY_TARGETS = QUEST_JOURNAL_DISCOVERY_VARIANTS.map(
  (variant) => variant.id
);

function normalizeStringArray(value, allowed = [], fallback = []) {
  if (!Array.isArray(value)) return [...fallback];
  const filtered = value.filter((item) => allowed.includes(item));
  return filtered.length ? filtered : [...fallback];
}

export const DEFAULT_QUEST_JOURNAL_FILTERS = {
  axisMode: QUEST_JOURNAL_FILTER_AXIS.INSTRUCTOR,
  refineByInstructor: false,
  themeCharacterId: "",
  instructorKind: QUEST_JOURNAL_INSTRUCTOR_KIND.ANY,
  characterId: "",
  questOutcome: QUEST_JOURNAL_QUEST_OUTCOME.ANY,
  relicOutcome: QUEST_JOURNAL_RELIC_OUTCOME.ANY,
  objectiveKind: QUEST_JOURNAL_OBJECTIVE_KIND.ANY,
  readingValidation: DEFAULT_READING_VALIDATION,
  masteryTargets: DEFAULT_MASTERY_TARGETS,
  discoveryTargets: DEFAULT_DISCOVERY_TARGETS,
  rewardKind: QUEST_JOURNAL_REWARD_KIND.ANY,
  completedObjectives: QUEST_JOURNAL_COMPLETED_OBJECTIVES.ANY,
  datePreset: QUEST_JOURNAL_DATE_PRESET.ALL,
  specificDate: "",
  dateFrom: "",
  dateTo: "",
};

export function maskQuestJournalDateInput(value = "") {
  const digits = String(value).replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function normalizeString(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function sanitizeQuestJournalFilters(raw = {}) {
  const source = raw && typeof raw === "object" ? raw : {};
  const axisMode =
    source.axisMode === QUEST_JOURNAL_FILTER_AXIS.THEME
      ? QUEST_JOURNAL_FILTER_AXIS.THEME
      : QUEST_JOURNAL_FILTER_AXIS.INSTRUCTOR;

  const filters = {
    axisMode,
    refineByInstructor: Boolean(source.refineByInstructor),
    themeCharacterId: normalizeString(source.themeCharacterId),
    instructorKind: Object.values(QUEST_JOURNAL_INSTRUCTOR_KIND).includes(source.instructorKind)
      ? source.instructorKind
      : QUEST_JOURNAL_INSTRUCTOR_KIND.ANY,
    characterId: normalizeString(source.characterId),
    questOutcome: Object.values(QUEST_JOURNAL_QUEST_OUTCOME).includes(source.questOutcome)
      ? source.questOutcome
      : QUEST_JOURNAL_QUEST_OUTCOME.ANY,
    relicOutcome: Object.values(QUEST_JOURNAL_RELIC_OUTCOME).includes(source.relicOutcome)
      ? source.relicOutcome
      : QUEST_JOURNAL_RELIC_OUTCOME.ANY,
    objectiveKind: Object.values(QUEST_JOURNAL_OBJECTIVE_KIND).includes(source.objectiveKind)
      ? source.objectiveKind
      : QUEST_JOURNAL_OBJECTIVE_KIND.ANY,
    readingValidation: normalizeStringArray(
      source.readingValidation,
      DEFAULT_READING_VALIDATION,
      DEFAULT_READING_VALIDATION
    ),
    masteryTargets: normalizeStringArray(
      source.masteryTargets,
      DEFAULT_MASTERY_TARGETS,
      DEFAULT_MASTERY_TARGETS
    ),
    discoveryTargets: normalizeStringArray(
      source.discoveryTargets,
      DEFAULT_DISCOVERY_TARGETS,
      DEFAULT_DISCOVERY_TARGETS
    ),
    rewardKind: Object.values(QUEST_JOURNAL_REWARD_KIND).includes(source.rewardKind)
      ? source.rewardKind
      : QUEST_JOURNAL_REWARD_KIND.ANY,
    completedObjectives: Object.values(QUEST_JOURNAL_COMPLETED_OBJECTIVES).includes(
      source.completedObjectives
    )
      ? source.completedObjectives
      : QUEST_JOURNAL_COMPLETED_OBJECTIVES.ANY,
    datePreset: Object.values(QUEST_JOURNAL_DATE_PRESET).includes(source.datePreset)
      ? source.datePreset
      : QUEST_JOURNAL_DATE_PRESET.ALL,
    specificDate: maskQuestJournalDateInput(source.specificDate || ""),
    dateFrom: maskQuestJournalDateInput(source.dateFrom || ""),
    dateTo: maskQuestJournalDateInput(source.dateTo || ""),
  };

  return filters;
}

export function prepareQuestJournalFiltersForApply(raw = {}) {
  const filters = sanitizeQuestJournalFilters(raw);

  if (filters.axisMode === QUEST_JOURNAL_FILTER_AXIS.THEME && filters.themeCharacterId) {
    filters.refineByInstructor = false;
    filters.characterId = filters.themeCharacterId;
  }

  return filters;
}

export function isQuestJournalFilterActive(filters = DEFAULT_QUEST_JOURNAL_FILTERS) {
  const normalized = prepareQuestJournalFiltersForApply(filters);
  const defaultNormalized = prepareQuestJournalFiltersForApply(DEFAULT_QUEST_JOURNAL_FILTERS);
  return JSON.stringify(normalized) !== JSON.stringify(defaultNormalized);
}

export const QUEST_JOURNAL_OBJECTIVE_OPTIONS = [
  { value: QUEST_JOURNAL_OBJECTIVE_KIND.ANY, label: "Sans distinction" },
  { value: QUEST_JOURNAL_OBJECTIVE_KIND.READING, label: "Objectif de lecture" },
  { value: QUEST_JOURNAL_OBJECTIVE_KIND.PLAY_GAMES, label: "Nombre de parties à jouer" },
  { value: QUEST_JOURNAL_OBJECTIVE_KIND.SCORE_TARGET, label: "Score à atteindre" },
  { value: QUEST_JOURNAL_OBJECTIVE_KIND.MASTERY, label: "Points de maîtrise à gagner" },
  { value: QUEST_JOURNAL_OBJECTIVE_KIND.DISCOVERY, label: "Points de découverte à gagner" },
  { value: QUEST_JOURNAL_OBJECTIVE_KIND.CAPTURE, label: "Capture de mot" },
  { value: QUEST_JOURNAL_OBJECTIVE_KIND.MEMORY, label: "Jeux de mémoire" },
  { value: QUEST_JOURNAL_OBJECTIVE_KIND.GRADE_IMPROVE, label: "Amélioration de graduation" },
];
